import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useLiveClock from '../../hooks/useLiveClock';
import useOutsideClick from '../../hooks/useOutsideClick';

const TopHUD = ({ currentTheme, onThemeChange }) => {
    const date = useLiveClock();
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();

    const closeMenu = useCallback(() => setMenuOpen(false), []);
    useOutsideClick(menuOpen, closeMenu);

    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dateNum = date.getDate();
    const hour = date.getHours();

    const isDay = hour >= 6 && hour < 18;
    const timeOfDayStr = isDay ? "DAY" : "NIGHT";

    return (
        <div
            className="fixed top-2 right-2 md:top-10 md:right-14 2xl:top-20 2xl:right-20 z-[200] flex items-start gap-2 md:gap-3 2xl:gap-4 transform rotate-0 md:rotate-2 2xl:rotate-3 scale-75 lg:scale-90 xl:scale-100 2xl:scale-110 origin-top-right"
            role="complementary"
            aria-label="HUD Clock and Theme Selector"
        >

            {/* Date/Back-to-V1 block — desktop only */}
            <div className="hidden md:flex flex-col items-end pointer-events-none">

                {/* Top Row: Month Date | Day */}
                <div className="bg-black text-white font-bebas text-4xl 2xl:text-5xl px-4 2xl:px-5 py-1 2xl:py-2 border-4 2xl:border-[6px] border-white shadow-[8px_8px_0_#000] 2xl:shadow-[12px_12px_0_#000] tracking-widest flex items-center gap-3 2xl:gap-4 pointer-events-auto">
                    <span>{monthName} {dateNum}</span>
                    <span>{dayName}</span>
                </div>

                {/* Bottom Row: Back to V1 Button and Day/Night Indicator */}
                <div className="flex flex-row items-center gap-3 mt-3 pointer-events-none">
                    <button
                        type="button"
                        onClick={() => navigate('/v1')}
                        className="bg-black text-white font-bebas text-2xl 2xl:text-3xl px-4 2xl:px-5 py-1 2xl:py-2 border-4 2xl:border-[6px] border-white shadow-[8px_8px_0_#000] 2xl:shadow-[12px_12px_0_#000] tracking-widest hover:bg-white hover:text-black transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FFF200] pointer-events-auto"
                    >
                        BACK TO V1
                    </button>

                    <div className="bg-white text-black font-black italic px-3 2xl:px-4 py-1 2xl:py-2 text-lg 2xl:text-xl border-4 2xl:border-[6px] border-black shadow-[8px_8px_0_#000] 2xl:shadow-[12px_12px_0_#000] -rotate-2 2xl:-rotate-3 uppercase pointer-events-none">
                        {timeOfDayStr}
                    </div>
                </div>

            </div>

            {/* Theme Selector — always visible */}
            <div className="relative pointer-events-auto" onClick={e => e.stopPropagation()}>
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-expanded={menuOpen}
                    aria-haspopup="true"
                    aria-label="Select color theme"
                    className="w-14 h-14 md:w-16 md:h-16 2xl:w-20 2xl:h-20 rounded-full relative hover:scale-110 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
                    title="Change Theme"
                >
                    {isDay ? (
                        <div className="w-full h-full bg-[#FFF200] rounded-full border-2 md:border-[4px] 2xl:border-[6px] border-black flex items-center justify-center text-black text-2xl md:text-3xl 2xl:text-4xl shadow-[3px_3px_0_#000] md:shadow-[4px_4px_0_#000] 2xl:shadow-[6px_6px_0_#000] animate-[spin_10s_linear_infinite] will-change-transform">
                            ☀
                        </div>
                    ) : (
                        <div className="w-full h-full bg-[#222] rounded-full border-2 md:border-[4px] 2xl:border-[6px] border-gray-400 flex items-center justify-center shadow-[3px_3px_0_#000] md:shadow-[4px_4px_0_#000] 2xl:shadow-[6px_6px_0_#000] relative overflow-hidden">
                            <div className="absolute inset-0 bg-[#FFF200] transform translate-x-1.5 -translate-y-1.5 md:translate-x-3 md:-translate-y-3 2xl:translate-x-[4.5rem] 2xl:-translate-y-[4.5rem] rounded-full"></div>
                        </div>
                    )}
                </button>

                {menuOpen && (
                    <div className="absolute top-full right-0 mt-4 2xl:mt-5 bg-black border-4 2xl:border-[6px] border-white p-3 2xl:p-4 flex flex-col gap-3 2xl:gap-4 shadow-[8px_8px_0_#000] 2xl:shadow-[12px_12px_0_#000] z-[200] origin-top-right animate-[hudDrop_0.18s_cubic-bezier(0.22,1,0.36,1)_forwards]" role="menu">
                        <button role="menuitem" onClick={() => { onThemeChange(''); setMenuOpen(false); }} className={`w-10 h-10 2xl:w-12 2xl:h-12 rounded-full bg-[#A30000] border-2 2xl:border-[3px] hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentTheme === '' ? 'border-white scale-110' : 'border-[#FFF200]'}`} title="Classic Red" />
                        <button role="menuitem" onClick={() => { onThemeChange('theme-blue'); setMenuOpen(false); }} className={`w-10 h-10 2xl:w-12 2xl:h-12 rounded-full bg-[#032BCC] border-2 2xl:border-[3px] hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentTheme === 'theme-blue' ? 'border-white scale-110' : 'border-[#00E5FF]'}`} title="Velvet Blue" />
                        <button role="menuitem" onClick={() => { onThemeChange('theme-green'); setMenuOpen(false); }} className={`w-10 h-10 2xl:w-12 2xl:h-12 rounded-full bg-[#009900] border-2 2xl:border-[3px] hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentTheme === 'theme-green' ? 'border-white scale-110' : 'border-[#FFF200]'}`} title="Matrix Green" />
                        <button role="menuitem" onClick={() => { onThemeChange('theme-mono'); setMenuOpen(false); }} className={`w-10 h-10 2xl:w-12 2xl:h-12 rounded-full bg-[#1A1A1A] border-2 2xl:border-[3px] hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentTheme === 'theme-mono' ? 'border-white scale-110' : 'border-white opacity-80'}`} title="High Contrast Mono" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopHUD;